import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { api } from '../lib/api';
import type { Ingestion } from '../lib/types';
import { Button, Callout, Dialog, IngestionBadge, useToast } from './ui';
import { ResolveForm } from './ResolveForm';
import { FilePreview } from './DocumentDrawer';
import { IconUpload } from './icons';

const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';

/** Manual upload: file → automatic extraction → user confirms / corrects → saved to a customer. */
export function UploadButton({ children, customerId, variant }: { children: ReactNode; customerId?: number; variant?: 'default' | 'primary' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>{children}</Button>
      {open ? <UploadDialog customerId={customerId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

function UploadDialog({ customerId, onClose }: { customerId?: number; onClose: () => void }) {
  const [ingestion, setIngestion] = useState<Ingestion | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const upload = async (file: File) => {
    setError(null);
    if (file.size > 20 * 1024 * 1024) return setError('הקובץ גדול מדי (מקסימום 20MB)');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const ing = await api<Ingestion>('/api/uploads', { form });
      setIngestion(ing);
      qc.invalidateQueries({ queryKey: ['ingestions'] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    // Uploaded but not saved: remove it from the review queue so it doesn't linger.
    if (ingestion && ingestion.status === 'needs_review') {
      await api(`/api/ingestions/${ingestion.id}/ignore`, { method: 'POST' }).catch(() => {});
      qc.invalidateQueries({ queryKey: ['ingestions'] });
      qc.invalidateQueries({ queryKey: ['agent-status'] });
    }
    onClose();
  };

  return (
    <Dialog open onClose={discard} title={t.profile.upload} size={ingestion ? 'xl' : undefined}>
      {!ingestion ? (
        <div className="stack">
          <div
            className={`dropzone ${over ? 'over' : ''}`}
            onClick={() => input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              const f = e.dataTransfer.files[0];
              if (f) void upload(f);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
          >
            {busy ? (
              <div className="row" style={{ justifyContent: 'center' }}>
                <span className="spinner" /> מעבד את המסמך…
              </div>
            ) : (
              <>
                <IconUpload size={22} />
                <div className="strong" style={{ marginTop: 8 }}>גררו קובץ לכאן או לחצו לבחירה</div>
                <div className="small muted">PDF, JPG, PNG · עד 20MB</div>
              </>
            )}
          </div>
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void upload(f);
              e.target.value = '';
            }}
          />
          {error ? <Callout tone="bad">{error}</Callout> : null}
          <p className="small muted">המערכת תזהה אוטומטית את סוג המסמך, המספר, התאריך, הסכום והלקוח. תוכלו לתקן כל פרט לפני השמירה.</p>
        </div>
      ) : ingestion.status === 'duplicate' ? (
        <div className="stack">
          <Callout tone="warn" title="המסמך כבר קיים במערכת">{ingestion.errorMessage}</Callout>
          <div className="row">
            {ingestion.duplicateOfDocumentId ? (
              <Button
                variant="primary"
                onClick={() => {
                  onClose();
                  navigate(`?doc=${ingestion.duplicateOfDocumentId}`);
                }}
              >
                פתיחת המסמך הקיים
              </Button>
            ) : null}
            <Button onClick={onClose}>{t.common.close}</Button>
          </div>
        </div>
      ) : ingestion.status === 'failed' ? (
        <div className="stack">
          <Callout tone="bad" title="לא ניתן לעבד את הקובץ">{ingestion.errorMessage}</Callout>
          <Button onClick={() => setIngestion(null)}>העלאת קובץ אחר</Button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 18, minHeight: 520 }} className="doc-layout">
          <div>
            <div className="row" style={{ marginBottom: 12 }}>
              <IngestionBadge status={ingestion.status} />
              <span className="small muted">{ingestion.attachmentName}</span>
            </div>
            <ResolveForm
              ingestion={ingestion}
              presetCustomerId={customerId}
              onDone={(r) => {
                onClose();
                navigate(`/customers/${r.customerId}?doc=${r.documentId}`);
              }}
            />
          </div>
          <div style={{ background: '#e9e9e5', borderRadius: 6, overflow: 'hidden', minHeight: 520 }}>
            <FilePreview file={ingestion.fileId ? { id: ingestion.fileId, mimeType: ingestion.fileMime ?? 'application/pdf' } : null} />
          </div>
        </div>
      )}
    </Dialog>
  );
}
